import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import { apiFetch } from "../api";
import "./Roasters.css";

const Roasters = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [roasters, setRoasters] = useState([]);
  const [error, setError] = useState("");
  const [newRoasterName, setNewRoasterName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  useEffect(() => {
    fetchRoasters();
  }, []);

  const fetchRoasters = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiFetch("/roasters");
      if (response.ok) {
        const data = await response.json();
        setRoasters(data);
      } else {
        throw new Error("Failed to fetch roasters");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newRoasterName.trim()) return;

    setError("");
    try {
      const response = await apiFetch("/roasters", {
        method: "POST",
        body: JSON.stringify({ name: newRoasterName.trim() }),
      });

      if (response.ok) {
        const newRoaster = await response.json();
        setRoasters([...roasters, newRoaster]);
        setNewRoasterName("");
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to create roaster");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStartEdit = (roaster) => {
    setEditingId(roaster.id);
    setEditingName(roaster.name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleSaveEdit = async (roasterId) => {
    if (!editingName.trim()) return;

    setError("");
    try {
      const response = await apiFetch(`/roasters/${roasterId}`, {
        method: "PATCH",
        body: JSON.stringify({ name: editingName.trim() }),
      });

      if (response.ok) {
        const updated = await response.json();
        setRoasters(
          roasters.map((r) => (r.id === roasterId ? updated : r))
        );
        setEditingId(null);
        setEditingName("");
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to update roaster");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (roasterId) => {
    if (!window.confirm("Are you sure? Batches linked to this roaster will become unlinked.")) {
      return;
    }

    setError("");
    try {
      const response = await apiFetch(`/roasters/${roasterId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setRoasters(roasters.filter((r) => r.id !== roasterId));
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to delete roaster");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Loading roasters...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Header user={user} onLogout={handleLogout} />

      <main className="dashboard-content roasters-content">
        <div className="roasters-header">
          <h2>My Roasters</h2>
          <p className="roasters-subtitle">
            Manage your coffee roasters and brands
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="create-roaster-section">
          <h3>Add New Roaster</h3>
          <form onSubmit={handleCreate} className="create-form">
            <input
              type="text"
              value={newRoasterName}
              onChange={(e) => setNewRoasterName(e.target.value)}
              placeholder="Enter roaster name (e.g., Counter Culture)"
              className="roaster-name-input"
              maxLength={255}
            />
            <button type="submit" className="btn-create">
              Add Roaster
            </button>
          </form>
        </div>

        <div className="roasters-list-section">
          <h3>Your Roasters ({roasters.length})</h3>
          {roasters.length === 0 ? (
            <p className="no-roasters-message">
              No roasters yet. Add one above to get started.
            </p>
          ) : (
            <div className="roasters-list">
              {roasters.map((roaster) => (
                <div key={roaster.id} className="roaster-card">
                  {editingId === roaster.id ? (
                    <div className="roaster-edit-mode">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="roaster-edit-input"
                        maxLength={255}
                        autoFocus
                      />
                      <div className="edit-actions">
                        <button
                          onClick={() => handleSaveEdit(roaster.id)}
                          className="btn-save"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="btn-cancel-edit"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="roaster-view-mode">
                      <div className="roaster-info">
                        <h4 className="roaster-name">{roaster.name}</h4>
                        <p className="roaster-date">
                          Added {new Date(roaster.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="roaster-actions">
                        <button
                          onClick={() => handleStartEdit(roaster)}
                          className="btn-edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(roaster.id)}
                          className="btn-delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="back-action">
          <button onClick={() => navigate("/dashboard")} className="btn-back">
            ← Back to Dashboard
          </button>
        </div>
      </main>
    </div>
  );
};

export default Roasters;
